import {
  invalidateMiniCartCache,
  readMiniCartCache,
  writeMiniCartCache,
  type MiniCartCachedQuery,
  type MiniCartCacheIdentity,
} from '@lib/commerce/miniCartCache';
import {
  hashKey,
  hydrate,
  type QueryClient,
  type QueryKey,
  type QueryState,
} from '@tanstack/react-query';
import {
  getGetCartQueryKey,
  getQueryProductProjectionsQueryKey,
  getValidateOrgCanSubscribeToItemsQueryKey,
  getValidateOrgCanUpgradeToItemsQueryKey,
  type Cart,
  type CartAddress,
} from '@unity/commerce-bridge';
import { getCartOrganizationId } from '@unity/commerce-core';
import axios from 'axios';

/**
 * Which of the island's reads are stored (CPS-7696): what the badge and the
 * popover draw first. The org and the user context are not: they carry the
 * org's address, tax details and subscriptions and the user's profile, and
 * the PDP's provider reads them anyway.
 */

/** commerce-core's cart query data. */
type CartQueryData = { cart?: Cart; hasTaxCalculationError?: boolean };

const PRODUCT_PROJECTIONS_KEY = getQueryProductProjectionsQueryKey();

const startsWith = (queryKey: QueryKey, prefix: QueryKey) =>
  prefix.every((part, index) => queryKey[index] === part);

const cartKeyOf = ({ userId }: MiniCartCacheIdentity) =>
  getGetCartQueryKey(`user:${userId}`);

const isCartKey = (queryKey: QueryKey, identity: MiniCartCacheIdentity) =>
  hashKey(queryKey) === hashKey(cartKeyOf(identity));

/** The ownership checks, for this org's cart. */
const isOrgCheckKey = (
  queryKey: QueryKey,
  { organizationId }: MiniCartCacheIdentity
) =>
  [
    getValidateOrgCanSubscribeToItemsQueryKey(organizationId),
    getValidateOrgCanUpgradeToItemsQueryKey(organizationId),
  ].some((key) => startsWith(queryKey, key.slice(0, -1)));

/**
 * commerce-core keys the cart by user alone, so after an org switch the cart
 * query holds the previous org's cart until it is reread.
 */
const belongsTo = (
  queryKey: QueryKey,
  data: unknown,
  identity: MiniCartCacheIdentity
): boolean => {
  if (isCartKey(queryKey, identity)) {
    const { cart } = data as CartQueryData;
    return (
      Boolean(cart) && getCartOrganizationId(cart!) === identity.organizationId
    );
  }
  return (
    startsWith(queryKey, PRODUCT_PROJECTIONS_KEY) ||
    isOrgCheckKey(queryKey, identity)
  );
};

const countryOnly = (address: CartAddress | undefined) =>
  address && { country: address.country };

/**
 * A cart reads the org's billing address, its VAT id and who made it; only
 * the countries are kept, as commerce-ui names a blocked one.
 */
const redactCart = ({
  anonymousId: _anonymousId,
  customerId: _customerId,
  customerEmail: _customerEmail,
  createdBy: _createdBy,
  lastModifiedBy: _lastModifiedBy,
  paymentInfo: _paymentInfo,
  itemShippingAddresses: _itemShippingAddresses,
  billingAddress,
  shippingAddress,
  custom,
  ...cart
}: Cart): Partial<Cart> => {
  const { vatId: _vatId, ...fields } = custom?.fields ?? {};
  return {
    ...cart,
    ...(custom && { custom: { ...custom, fields } as Cart['custom'] }),
    ...(billingAddress && { billingAddress: countryOnly(billingAddress) }),
    ...(shippingAddress && { shippingAddress: countryOnly(shippingAddress) }),
  };
};

const redact = (
  queryKey: QueryKey,
  data: unknown,
  identity: MiniCartCacheIdentity
): unknown => {
  if (!isCartKey(queryKey, identity)) return data;
  const { cart, ...rest } = data as CartQueryData;
  return { ...rest, cart: redactCart(cart!) };
};

const restoredState = ({
  data,
  dataUpdatedAt,
}: MiniCartCachedQuery): QueryState => ({
  data,
  dataUpdateCount: 1,
  dataUpdatedAt,
  error: null,
  errorUpdateCount: 0,
  errorUpdatedAt: 0,
  fetchFailureCount: 0,
  fetchFailureReason: null,
  fetchMeta: null,
  isInvalidated: false,
  status: 'success',
  fetchStatus: 'idle',
});

/**
 * A refused credential, or a cart version that moved on. A write failing so
 * settles, which invalidates anyway (see createMiniCartQueryClient).
 */
const INVALIDATING_STATUSES = new Set([401, 412]);

const statusOf = (error: unknown) =>
  axios.isAxiosError(error) ? (error.response?.status ?? 0) : 0;

/**
 * The cart is gone (ordered, say): a read that started before this would
 * bring it back to new tabs.
 */
const isCartGone = (
  queryKey: QueryKey,
  error: unknown,
  identity: MiniCartCacheIdentity | undefined
) =>
  identity !== undefined &&
  isCartKey(queryKey, identity) &&
  statusOf(error) === 404;

export type MiniCartPersister = {
  /**
   * Stores the client's reads as they land, until the returned stop. Start it
   * before the provider mounts: a read whose start was missed is not stored.
   */
  start: (queryClient: QueryClient) => () => void;
  /**
   * The shopper the island now reads for; their stored reads are restored
   * into the client. `undefined` stops storing.
   */
  follow: (
    identity: MiniCartCacheIdentity | undefined,
    currency: string
  ) => void;
  /** A cart change: nothing stored before now is served again. */
  invalidate: () => void;
};

/** Keeps the mini-cart island's reads in `miniCartCache` (CPS-7696). */
export const createMiniCartPersister = (): MiniCartPersister => {
  let queryClient: QueryClient | undefined;
  let identity: MiniCartCacheIdentity | undefined;
  // By query hash: when the read now in flight started (a retry restarts
  // it), and when the one that produced the data held started.
  const readStartedAt = new Map<string, number>();
  const validSince = new Map<string, number>();

  const invalidate = () => invalidateMiniCartCache(identity);

  const persist = (client: QueryClient, current: MiniCartCacheIdentity) => {
    const queries = client
      .getQueryCache()
      .getAll()
      .flatMap(({ queryKey, queryHash, state }): MiniCartCachedQuery[] => {
        const since = validSince.get(queryHash);
        return since !== undefined &&
          state.status === 'success' &&
          !state.isInvalidated &&
          belongsTo(queryKey, state.data, current)
          ? [
              {
                queryKey,
                data: redact(queryKey, state.data, current),
                dataUpdatedAt: state.dataUpdatedAt,
                validSince: since,
              },
            ]
          : [];
      });
    writeMiniCartCache(current, queries);
  };

  const start = (client: QueryClient) => {
    queryClient = client;
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const { action, query } = event;
      if (action.type === 'fetch' || action.type === 'failed') {
        readStartedAt.set(query.queryHash, Date.now());
      } else if (action.type === 'error') {
        if (
          INVALIDATING_STATUSES.has(statusOf(action.error)) ||
          isCartGone(query.queryKey, action.error, identity)
        ) {
          invalidate();
        }
      } else if (action.type === 'success') {
        // Written straight into the cache: a write's response, current now,
        // unless it keeps the time of the read it rewrites (a resync, see
        // createMiniCartQueryClient), which it is no newer than.
        const since = !action.manual
          ? readStartedAt.get(query.queryHash)
          : action.dataUpdatedAt === undefined
            ? Date.now()
            : validSince.get(query.queryHash);
        if (since === undefined) validSince.delete(query.queryHash);
        else validSince.set(query.queryHash, since);
        if (identity && belongsTo(query.queryKey, query.state.data, identity)) {
          persist(client, identity);
        }
      }
    });
    return () => {
      unsubscribe();
      queryClient = undefined;
    };
  };

  const follow = (
    next: MiniCartCacheIdentity | undefined,
    currency: string
  ) => {
    identity = next;
    if (!next || !queryClient) return;
    const client = queryClient;
    // syncCartCurrency would re-price a cart read in another currency.
    const restorable = readMiniCartCache(next).filter(
      ({ queryKey, data }) =>
        belongsTo(queryKey, data, next) &&
        (!isCartKey(queryKey, next) ||
          (data as CartQueryData).cart?.totalPrice?.currencyCode === currency)
    );
    hydrate(client, {
      mutations: [],
      queries: restorable.map((query) => ({
        queryKey: query.queryKey,
        queryHash: hashKey(query.queryKey),
        state: restoredState(query),
        dehydratedAt: query.dataUpdatedAt,
      })),
    });
    // A query holding a newer read keeps it, and its own start.
    for (const query of restorable) {
      const queryHash = hashKey(query.queryKey);
      const state = client.getQueryCache().get(queryHash)?.state;
      if (state?.dataUpdatedAt === query.dataUpdatedAt) {
        validSince.set(queryHash, query.validSince);
      }
    }
  };

  return { start, follow, invalidate };
};
